import React, { useCallback, useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
    clearCreateProductState,
    createProduct,
} from "../../store/productsSlice";
import {
    getMinimumLettersMessage,
    hasMinimumLetters,
} from "../../utils/textValidation";

const CreateProductModal = ({
    isOpen,
    onClose,
    organizationId,
    onProductCreated,
}) => {
    const dispatch = useDispatch();
    const { createProductStatus } = useSelector(
        (state) => state.products,
    );
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");

    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    const isCreating = createProductStatus === "loading";
    const isCreateDisabled =
        isCreating ||
        !hasMinimumLetters(trimmedName) ||
        !hasMinimumLetters(trimmedDescription);

    const resetLocalState = useCallback(() => {
        setName("");
        setDescription("");
        dispatch(clearCreateProductState());
    }, [dispatch]);

    const handleClose = useCallback(() => {
        resetLocalState();
        onClose();
    }, [onClose, resetLocalState]);

    useEffect(() => {
        if (!isOpen) return;

        resetLocalState();
    }, [isOpen, resetLocalState]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                handleClose();
            }
        };

        window.addEventListener("keydown", handleEscape);

        return () => window.removeEventListener("keydown", handleEscape);
    }, [handleClose, isOpen]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!trimmedName) {
            toast.error("Product name is required.");
            return;
        }

        if (!hasMinimumLetters(trimmedName)) {
            toast.error(getMinimumLettersMessage("Product name"));
            return;
        }

        if (!trimmedDescription) {
            toast.error("Product description is required.");
            return;
        }

        if (!hasMinimumLetters(trimmedDescription)) {
            toast.error(getMinimumLettersMessage("Product description"));
            return;
        }

        try {
            const createdProduct = await dispatch(
                createProduct({
                    name: trimmedName,
                    description: trimmedDescription,
                    organizationId,
                }),
            ).unwrap();

            await onProductCreated?.(createdProduct);
            handleClose();
        } catch (error) {
            toast.error(
                error || "Unable to create product. Please try again.",
            );
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="app-fade-in fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    handleClose();
                }
            }}
        >
            <section
                role="dialog"
                aria-modal="true"
                aria-labelledby="create-product-title"
                className="app-modal-enter flex w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <header className="flex items-start justify-between gap-4 border-b border-indigo-100 bg-[#eef2ff] px-5 py-4">
                    <div>
                        <h2
                            id="create-product-title"
                            className="text-xl font-bold text-slate-900"
                        >
                            Create Product
                        </h2>
                        <p className="mt-1 text-sm text-[#475569]">
                            Add a product you can use in outreach campaigns.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={handleClose}
                        aria-label="Close"
                        title="Close"
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-indigo-50 hover:text-indigo-700"
                    >
                        <X size={20} aria-hidden="true" />
                    </button>
                </header>

                <form onSubmit={handleSubmit} className="px-5 py-5">
                    <div className="space-y-4">
                        <div>
                            <label
                                htmlFor="create-product-name"
                                className="mb-1.5 block text-sm font-semibold text-slate-700"
                            >
                                Product Name
                            </label>
                            <input
                                id="create-product-name"
                                type="text"
                                value={name}
                                onChange={(event) => {
                                    setName(event.target.value);
                                    dispatch(clearCreateProductState());
                                }}
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="create-product-description"
                                className="mb-1.5 block text-sm font-semibold text-slate-700"
                            >
                                Product Description
                            </label>
                            <textarea
                                id="create-product-description"
                                rows={5}
                                value={description}
                                onChange={(event) => {
                                    setDescription(event.target.value);
                                    dispatch(clearCreateProductState());
                                }}
                                className="w-full resize-y rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                            />
                        </div>
                    </div>

                    <footer className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isCreateDisabled}
                            className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {isCreating && (
                                <Loader2
                                    size={16}
                                    className="animate-spin"
                                    aria-hidden="true"
                                />
                            )}
                            {isCreating ? "Creating..." : "Create Product"}
                        </button>
                    </footer>
                </form>
            </section>
        </div>
    );
};

export default CreateProductModal;
